'use client';

import { HStack } from '@terra/ui-styled-system';
import {
  BodyStrong,
  IconButton,
  McpServerStatusBadge,
  MetaDefault,
  SettingsBooleanControl,
  SettingsRow,
  Tooltip,
} from '@terra/ui-design-system';
import {
  CableIcon,
  Delete01Icon,
  Edit03Icon,
  TestTube02Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { McpServerResponseDto } from '@terra/shared-contracts-mcp-servers';
import { getMcpServerDisplayName } from '@terra/shared-utils';

type McpServerRowProps = {
  server: McpServerResponseDto;
  isToggling: boolean;
  isTesting: boolean;
  onToggleEnabled: (enabled: boolean) => void;
  onTest: () => void;
  onEdit: () => void;
  onDelete: () => void;
};

export function McpServerRow({
  server,
  isToggling,
  isTesting,
  onToggleEnabled,
  onTest,
  onEdit,
  onDelete,
}: McpServerRowProps) {
  const t = useTranslations('settings.mcpServerForm');

  return (
    <SettingsRow
      leadingIcon={<CableIcon size={16} />}
      label={
        <HStack gap={2} alignItems="center" flexWrap="wrap">
          <BodyStrong>{getMcpServerDisplayName(server)}</BodyStrong>
          <McpServerStatusBadge
            server={server}
            statusConnected={t('statusConnected')}
            statusDegraded={t('statusDegraded')}
            statusFailing={t('statusFailing')}
            statusDisabled={t('statusDisabled')}
            statusNotTested={t('statusNotTested')}
          />
        </HStack>
      }
      description={[
        server.url,
        ...server.allowedTargetTypes,
        server.requiresApproval ? t('approvalRequired') : null,
      ]
        .filter(Boolean)
        .join(' · ')}
      action={{
        label: t('edit'),
        icon: <Edit03Icon size={16} />,
        onClick: onEdit,
      }}
      control={
        <HStack gap={2} alignItems="center">
          {server.lastError ? (
            <MetaDefault color="fg.error" truncate maxW="320px">
              {server.lastError}
            </MetaDefault>
          ) : null}
          <SettingsBooleanControl
            checked={server.enabled}
            onCheckedChange={(details) => onToggleEnabled(details.checked)}
            disabled={isToggling}
          />
          <Tooltip title={t('testConnection')}>
            <IconButton
              variant="ghost"
              size="xs"
              aria-label={t('testConnection')}
              onClick={onTest}
              loading={isTesting}
            >
              <TestTube02Icon size={14} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('removeServer')}>
            <IconButton
              variant="ghost"
              size="xs"
              aria-label={t('removeServer')}
              onClick={onDelete}
            >
              <Delete01Icon size={14} />
            </IconButton>
          </Tooltip>
        </HStack>
      }
    />
  );
}
